import { useState, useCallback } from 'react';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getQuotationsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { 
  Quotation, 
  SearchQuotationsRequest, 
  ACLAuthorizationRequest,
  QuotationResponse,
  SearchQuotationsPostContentTypeEnum,
  CreateQuotationContentTypeEnum
} from '@/api/bol';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseQuotationsReturn {
  quotations: QuotationResponse[];
  loading: boolean;
  searchError: string | null;
  selectedQuotation: QuotationResponse | null;
  setSelectedQuotation: (quotation: QuotationResponse | null) => void;
  fetchQuotations: (page?: number, pageSize?: number, searchBody?: SearchQuotationsRequest) => Promise<boolean>;
  createQuotation: (quotationData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteQuotation: (quotationId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useQuotations = (): UseQuotationsReturn => {
  const [quotations, setQuotations] = useState<QuotationResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedQuotation, setSelectedQuotation] = useState<QuotationResponse | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchQuotations = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchQuotationsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);

    console.log('Fetching quotations with:', { page, pageSize, searchBody: normalizedSearchBody });

    setLoading(true);
    setSearchError(null);
    
    try {
      const api = getQuotationsApi();
      
      const response = await api.searchQuotationsPost({
        contentType: SearchQuotationsPostContentTypeEnum.ApplicationJson,
        searchQuotationsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('Quotations API response:', response);
      
      setQuotations(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      console.error('Error fetching quotations:', err);
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch quotations');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createQuotation = useCallback(async (quotationData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      console.log('Creating quotation with data:', quotationData, 'Member:', memberIdentifier, 'ACL:', aclAuthorizations);
      
      const api = getQuotationsApi();
      
      const createdQuotation = await api.createQuotation({
        contentType: CreateQuotationContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        quotation: quotationData as Quotation,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined
      });
      
      console.log('Quotation created successfully:', createdQuotation);
      const createdQuotationId = createdQuotation?.id;
      
      showSuccess(
        'Quotation created successfully',
        createdQuotationId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('quotation', createdQuotationId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      console.error('Error creating quotation:', err);
      const errorMessage = await formatOperationError(err, 'Create quotation', 'Failed to create quotation');
      showError(errorMessage);
      throw err; // Re-throw to let the calling component handle form state
    }
  }, [showSuccess, showError]);

  const deleteQuotation = useCallback(async (quotationId: string) => {
    try {
      const api = getQuotationsApi();
      
      await api.deleteQuotationById({
        quotationId
      });
      
      showSuccess('Quotation deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting quotation:', err);
      const errorMessage = await formatOperationError(err, 'Delete quotation', 'Failed to delete quotation');
      showError(errorMessage);
      throw err; // Re-throw to let the calling component handle dialog state
    }
  }, [showSuccess, showError]);

  return {
    quotations,
    loading,
    searchError,
    selectedQuotation,
    setSelectedQuotation,
    fetchQuotations,
    createQuotation,
    deleteQuotation,
    setTotal,
    total
  };
};